import * as styles from './marketingFeatureNamedConfigurationFormCssDefs';
import { Button, FormTextField, Grid, IconButton as TockIconButton, Typography } from 'ui';
import { FieldArray, Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { MarketingFeaturesWidgetPreview } from 'components/MarketingFeaturesWidgetPreview';
import { consumer } from '@tock/coretypes';
import { useActions } from 'utils/connect';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

interface Props {
  updatingConfig?: { id?: number; name: string; isArchived: boolean };
  initialValues: MarketingFeatureFormFields;
  onSubmit(
    values: MarketingFeatureFormFields,
    formikHelpers: FormikHelpers<MarketingFeatureFormFields>
  ): Promise<void>;
  onClear(): void;
}

export interface MarketingFeatureFormFields {
  name: string;
  tiles: consumer.IMarketingFeatureTile[];
}

const emptyTile = { title: '', text: '', link: '', imageUrl: '' };

export const emptyMfw: MarketingFeatureFormFields = {
  name: '',
  tiles: [emptyTile],
};

const FETCH_MODE = 'ALL';

const MarketingFeatureNamedConfigurationForm: React.FunctionComponent<
  React.PropsWithChildren<Props>
> = ({ updatingConfig, initialValues, onSubmit, onClear }) => {
  const { actions } = useActions();

  return (
    <Formik<MarketingFeatureFormFields>
      enableReinitialize
      initialValues={initialValues}
      onSubmit={onSubmit}
    >
      {({
        values,
        isSubmitting,
        setSubmitting,
        setValues,
      }: FormikProps<MarketingFeatureFormFields>) => {
        const saveButton = (
          <Button
            disabled={(values.tiles?.length ?? 0) < 3 || isSubmitting}
            color="success"
            size="large"
            type="submit"
          >
            {updatingConfig?.id ? 'Update' : 'Create'} Config
          </Button>
        );

        const removeCurrentConfig = async () => {
          if (updatingConfig?.id && confirm('configRemove')) {
            setSubmitting(true);
            await actions.consumerPage.deleteNamedConfiguration(updatingConfig?.id);
            await actions.consumerPage.fetchAllNamedConfigurations(FETCH_MODE);
            onClear();
            setSubmitting(false);
          }
        };

        const toggleCurrentConfigArchived = async () => {
          if (
            updatingConfig &&
            confirm(updatingConfig.isArchived ? 'configUnarchive' : 'configArchive')
          ) {
            setSubmitting(true);
            await actions.consumerPage.updateNamedConfiguration({
              ...updatingConfig,
              isArchived: !updatingConfig.isArchived,
            });
            await actions.consumerPage.fetchAllNamedConfigurations(FETCH_MODE);
            setSubmitting(false);
          }
        };

        return (
          <Form css={styles.content}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="display1Bold" mb={2}>
                  {updatingConfig?.id
                    ? `Updating Configuration, "${updatingConfig.name}" `
                    : 'New Configuration'}
                </Typography>
              </Grid>
              <Grid item xs={8}>
                <FormTextField
                  disabled={!!updatingConfig?.id}
                  fullWidth
                  label="Config Name (permanent, unique)"
                  name="name"
                  required
                />
              </Grid>
              <Grid item xs={4}>
                <Grid container justifyContent="flex-end">
                  {saveButton}
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <MarketingFeaturesWidgetPreview tiles={values.tiles} />
              </Grid>
              <FieldArray
                name="tiles"
                render={(arrayHelpers) => (
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      {(values.tiles ?? []).map((tile, index) => (
                        <Grid
                          item
                          xs={12}
                          key={`${values.name}-${index}`}
                          data-testid={`tile-${index}`}
                          css={styles.item}
                        >
                          <div className="item-inner">
                            <Grid container spacing={2} css={styles.itemInputs}>
                              <Grid item xs={12} md={6}>
                                <Grid container spacing={2}>
                                  <Grid item xs={12}>
                                    <FormTextField
                                      id={`tiles[${index}].title`}
                                      fullWidth
                                      label="Title"
                                      name={`tiles[${index}].title`}
                                      required
                                    />
                                  </Grid>
                                  <Grid item xs={12}>
                                    <FormTextField
                                      id={`tiles[${index}].text`}
                                      fullWidth
                                      label="Description"
                                      multiline
                                      name={`tiles[${index}].text`}
                                      required
                                      rows={3}
                                    />
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Grid container spacing={2}>
                                  <Grid item xs={12}>
                                    <FormTextField
                                      id={`tiles[${index}].link`}
                                      fullWidth
                                      label="Destination URL"
                                      name={`tiles[${index}].link`}
                                      required
                                    />
                                  </Grid>
                                  <Grid item xs={12}>
                                    <FormTextField
                                      id={`tiles[${index}].imageUrl`}
                                      fullWidth
                                      label="Image URL"
                                      name={`tiles[${index}].imageUrl`}
                                      required
                                      type="url"
                                    />
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                            <div css={styles.itemActions}>
                              <TockIconButton
                                aria-label="move up"
                                color="tertiary"
                                type="button"
                                variant="contained"
                                disabled={index === 0}
                                onClick={() => {
                                  arrayHelpers.move(index, index - 1);
                                }}
                              >
                                <KeyboardArrowUpIcon fontSize="small" />
                              </TockIconButton>
                              <TockIconButton
                                aria-label="delete"
                                color="danger"
                                type="button"
                                variant="text"
                                disabled={(values.tiles?.length ?? 0) === 1}
                                onClick={() => {
                                  if (confirm('itemRemove')) {
                                    arrayHelpers.remove(index);
                                  }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </TockIconButton>
                              <TockIconButton
                                aria-label="move down"
                                color="tertiary"
                                type="button"
                                variant="contained"
                                disabled={index >= (values.tiles?.length ?? 0) - 1}
                                onClick={() => arrayHelpers.move(index, index + 1)}
                              >
                                <KeyboardArrowDownIcon fontSize="small" />
                              </TockIconButton>
                            </div>
                          </div>
                        </Grid>
                      ))}
                      <Grid item xs={12}>
                        <Grid container justifyContent="flex-end">
                          <Button
                            disabled={(values.tiles?.length ?? 0) >= 5}
                            onClick={() => arrayHelpers.push(emptyTile)}
                          >
                            Add Item
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              />
              <Grid item xs={6}>
                <Grid container>
                  {updatingConfig?.id && (
                    <Button size="large" color="primary" onClick={toggleCurrentConfigArchived}>
                      {updatingConfig.isArchived ? 'Unarchive' : 'Archive'} Config
                    </Button>
                  )}
                  &nbsp;
                  {updatingConfig?.id && (
                    <Button
                      size="large"
                      color="danger"
                      variant="text"
                      onClick={removeCurrentConfig}
                    >
                      Delete Config
                    </Button>
                  )}
                </Grid>
              </Grid>
              <Grid item xs={6}>
                <Grid container justifyContent="flex-end">
                  {saveButton}
                </Grid>
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};

export default MarketingFeatureNamedConfigurationForm;
